import * as Sentry from "@sentry/react"

import { redactTokens } from "@/lib/redact-tokens"
import { router } from "@/router"

/** Outgoing URLs that receive `sentry-trace` / `baggage` for distributed tracing. */
function tracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = ["localhost"]
  const apiUrl = import.meta.env.VITE_API_URL
  if (typeof apiUrl === "string" && apiUrl.length > 0) {
    try {
      targets.push(new URL(apiUrl).origin)
    } catch {
      // ignore invalid VITE_API_URL
    }
  }
  return targets
}

/**
 * Initialize the browser Sentry SDK (no-op when `VITE_SENTRY_DSN` is unset).
 * Call before `createRoot` / first render so instrumentation wraps the app from the start.
 *
 * Option notes mirror Sentry’s React quickstart; we use `tanstackRouterBrowserTracingIntegration`
 * instead of `browserTracingIntegration()` so route names match TanStack Router.
 */
export function initBrowserSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    return
  }

  const isDev = import.meta.env.DEV

  Sentry.init({
    // Client DSN from Sentry → Project Settings → Client Keys (use env, never commit secrets).
    dsn,

    // VITE_SENTRY_ENVIRONMENT overrides Vite MODE so test and prod CF Pages builds are
    // distinguishable (both would otherwise resolve to "production" from MODE).
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,

    // No automatic IP / cookie / header collection; identity comes from the explicit `Sentry.setUser`.
    sendDefaultPii: false,

    // Token-bearing URLs (see redact-tokens.ts) can sit anywhere: request URL, Referer, breadcrumbs, spans.
    beforeSend: (event) => redactTokens(event),
    beforeSendTransaction: (event) => redactTokens(event),

    integrations: [
      // TanStack Router variant of browser tracing (min @tanstack/react-router 1.64.0 per Sentry).
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        // Custom recording events only (breadcrumbs, navigation, network spans); DOM snapshots never reach it.
        beforeAddRecordingEvent: (event) => redactTokens(event),
      }),
      // In-app feedback widget: floating button → name/email/message form, follows OS color scheme.
      // `triggerLabel: ""` renders the launcher icon-only (its mobile look) at every width; aria label keeps it accessible.
      Sentry.feedbackIntegration({ colorScheme: "system", triggerLabel: "", triggerAriaLabel: "Report a Bug" }),
    ],

    // Tracing: fraction of transactions captured (1.0 = 100%). Higher volume uses quota; we use
    // 100% in local dev and a lower rate for production builds.
    tracesSampleRate: isDev ? 1.0 : 0.2,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    // (only matching requests get `sentry-trace` / `baggage` headers — avoids leaking them to third parties).
    tracePropagationTargets: tracePropagationTargets(),

    // Session Replay: sample rate for normal sessions (10% in prod builds; 100% while developing locally).
    replaysSessionSampleRate: isDev ? 1.0 : 0.1,

    // If you're not already sampling the entire session, sample at 100% for sessions where an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Sentry docs also show `enableLogs: true` to forward logs to Sentry; our pinned SDK’s init typings
    // do not include it yet — add when you upgrade, or wire `consoleLoggingIntegration()` if needed.
  })

  // Replay events (their `urls` list) and bug-report feedback skip `beforeSend`; only an event processor sees them.
  Sentry.addEventProcessor((event) => redactTokens(event))
}
